import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import api from '../../services/api';

interface PLAccount {
  code: string;
  name: string;
  group: string;
  amount: number;
}

interface PLSummary {
  total_revenue: number;
  total_cogs: number;
  gross_profit: number;
  gross_margin: number;
  total_operating_expenses: number;
  net_profit: number;
  net_margin: number;
}

interface PLData {
  period: { from_date: string | null; to_date: string | null };
  revenue: PLAccount[];
  cost_of_sales: PLAccount[];
  operating_expenses: PLAccount[];
  summary: PLSummary;
}

const fmt = (n: number) =>
  `LKR ${Math.abs(n).toLocaleString('en-LK', { minimumFractionDigits: 2 })}`;

const ProfitLoss: React.FC = () => {
  const navigate = useNavigate();
  const now = new Date();
  const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
  const today = now.toISOString().slice(0, 10);

  const [fromDate, setFromDate] = useState(firstOfMonth);
  const [toDate, setToDate] = useState(today);
  const [data, setData] = useState<PLData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/accounting/reports/profit-loss', {
        params: { from_date: fromDate, to_date: toDate },
      });
      setData(res.data.data);
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const s = data?.summary;

  const AccountSection = ({
    title,
    accounts,
    isExpense = false,
  }: {
    title: string;
    accounts: PLAccount[];
    isExpense?: boolean;
  }) => (
    <>
      <SectionHeader>
        <TableCell colSpan={3}>{title}</TableCell>
      </SectionHeader>
      {accounts.length === 0 ? (
        <TableRow>
          <TableCell colSpan={3} style={{ color: '#999', fontStyle: 'italic', padding: '8px 20px' }}>
            No activity in this period
          </TableCell>
        </TableRow>
      ) : (
        accounts.map((a) => (
          <TableRow key={a.code} className="hover">
            <TableCell style={{ fontFamily: 'monospace', fontSize: '12px', color: '#666', width: '80px' }}>
              {a.code}
            </TableCell>
            <TableCell>{a.name}</TableCell>
            <TableCell style={{ textAlign: 'right', fontFamily: 'monospace' }}>
              {isExpense && a.amount < 0 ? (
                <span style={{ color: '#c62828' }}>{`(${fmt(a.amount)})`}</span>
              ) : (
                fmt(a.amount)
              )}
            </TableCell>
          </TableRow>
        ))
      )}
    </>
  );

  return (
    <Container>
      <Header>
        <BackButton onClick={() => navigate('/accounting/reports')}>← Back</BackButton>
        <div>
          <h1>Profit & Loss Statement</h1>
          <Subtitle>Revenue, cost of sales and operating expenses for a selected period</Subtitle>
        </div>
      </Header>

      <FilterCard>
        <FilterRow>
          <label>
            From Date:
            <DateInput
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </label>
          <label>
            To Date:
            <DateInput
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </label>
          <LoadButton onClick={load} disabled={loading}>
            {loading ? 'Loading...' : 'Load Report'}
          </LoadButton>
          {data && (
            <ExportButton onClick={() => window.print()}>Export PDF</ExportButton>
          )}
        </FilterRow>
      </FilterCard>

      {error && <ErrorAlert>{error}</ErrorAlert>}

      {!data && !loading && (
        <EmptyState>
          <EmptyIcon>📈</EmptyIcon>
          <p>Select a date range and click "Load Report" to view the Profit & Loss Statement</p>
        </EmptyState>
      )}

      {data && s && (
        <>
          <SummaryGrid>
            <SummaryCard color="#2e7d32">
              <SummaryLabel>Revenue</SummaryLabel>
              <SummaryValue>{fmt(s.total_revenue)}</SummaryValue>
            </SummaryCard>
            <SummaryCard color="#f57c00">
              <SummaryLabel>Operating Expenses</SummaryLabel>
              <SummaryValue>{fmt(s.total_operating_expenses)}</SummaryValue>
            </SummaryCard>
            <SummaryCard color="#1976d2">
              <SummaryLabel>Gross Profit</SummaryLabel>
              <SummaryValue>{fmt(s.gross_profit)}</SummaryValue>
              <SummarySubtext>Margin: {s.gross_margin.toFixed(1)}%</SummarySubtext>
            </SummaryCard>
            <SummaryCard color={s.net_profit >= 0 ? '#2e7d32' : '#c62828'}>
              <SummaryLabel>Net Profit</SummaryLabel>
              <SummaryValue style={{ color: s.net_profit >= 0 ? '#2e7d32' : '#c62828' }}>
                {fmt(s.net_profit)}
              </SummaryValue>
              <SummarySubtext>Margin: {s.net_margin.toFixed(1)}%</SummarySubtext>
            </SummaryCard>
          </SummaryGrid>

          <ReportCard>
            <ReportHeader>
              <ReportTitle>Profit & Loss Statement</ReportTitle>
              <ReportDate>
                {data.period.from_date} to {data.period.to_date}
              </ReportDate>
            </ReportHeader>

            <Table>
              <tbody>
                {/* REVENUE */}
                <AccountSection title="REVENUE" accounts={data.revenue} />
                <SubtotalRow color="rgba(46,125,50,0.1)">
                  <TableCell colSpan={2}>Total Revenue</TableCell>
                  <TableCell style={{ textAlign: 'right', fontFamily: 'monospace', color: '#2e7d32' }}>
                    {fmt(s.total_revenue)}
                  </TableCell>
                </SubtotalRow>

                {/* COST OF SALES */}
                {data.cost_of_sales.length > 0 && (
                  <>
                    <AccountSection title="COST OF SALES" accounts={data.cost_of_sales} isExpense />
                    <SubtotalRow color="rgba(198,40,40,0.08)">
                      <TableCell colSpan={2}>Total Cost of Sales</TableCell>
                      <TableCell style={{ textAlign: 'right', fontFamily: 'monospace', color: '#c62828' }}>
                        {fmt(s.total_cogs)}
                      </TableCell>
                    </SubtotalRow>
                  </>
                )}

                {/* GROSS PROFIT */}
                <GrossProfitRow>
                  <TableCell colSpan={2}>Gross Profit</TableCell>
                  <TableCell style={{ textAlign: 'right', fontFamily: 'monospace' }}>
                    {fmt(s.gross_profit)}
                  </TableCell>
                </GrossProfitRow>

                {/* OPERATING EXPENSES */}
                <AccountSection title="OPERATING EXPENSES" accounts={data.operating_expenses} isExpense />
                <SubtotalRow color="rgba(245,124,0,0.1)">
                  <TableCell colSpan={2}>Total Operating Expenses</TableCell>
                  <TableCell style={{ textAlign: 'right', fontFamily: 'monospace', color: '#f57c00' }}>
                    {fmt(s.total_operating_expenses)}
                  </TableCell>
                </SubtotalRow>

                {/* NET PROFIT */}
                <NetProfitRow isProfit={s.net_profit >= 0}>
                  <TableCell colSpan={2}>Net Profit (Loss)</TableCell>
                  <TableCell style={{ textAlign: 'right', fontFamily: 'monospace' }}>
                    {s.net_profit < 0 ? `(${fmt(s.net_profit)})` : fmt(s.net_profit)}
                  </TableCell>
                </NetProfitRow>
              </tbody>
            </Table>
          </ReportCard>
        </>
      )}
    </Container>
  );
};

const Container = styled.div`
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 30px;

  h1 {
    font-size: 28px;
    color: #333;
    margin-bottom: 5px;
  }
`;

const Subtitle = styled.p`
  color: #666;
  font-size: 14px;
`;

const BackButton = styled.button`
  background: white;
  border: 1px solid #ddd;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;

  &:hover {
    background: #f5f5f5;
    border-color: #667eea;
    color: #667eea;
  }
`;

const FilterCard = styled.div`
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin-bottom: 20px;
`;

const FilterRow = styled.div`
  display: flex;
  gap: 15px;
  align-items: center;
  flex-wrap: wrap;

  label {
    display: flex;
    flex-direction: column;
    gap: 5px;
    font-size: 14px;
    font-weight: 500;
    color: #555;
  }
`;

const DateInput = styled.input`
  padding: 10px 15px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  min-width: 160px;

  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const LoadButton = styled.button`
  padding: 10px 24px;
  background: #667eea;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 20px;

  &:hover:not(:disabled) {
    background: #5568d3;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ExportButton = styled.button`
  padding: 10px 24px;
  background: white;
  color: #667eea;
  border: 2px solid #667eea;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 20px;

  &:hover {
    background: #667eea;
    color: white;
  }
`;

const ErrorAlert = styled.div`
  background: #fee;
  color: #c00;
  padding: 15px 20px;
  border-radius: 6px;
  margin-bottom: 20px;
  border: 1px solid #fcc;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 80px 20px;
  color: #666;
`;

const EmptyIcon = styled.div`
  font-size: 64px;
  opacity: 0.3;
  margin-bottom: 20px;
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
`;

const SummaryCard = styled.div<{ color: string }>`
  background: white;
  padding: 20px;
  border-radius: 8px;
  border-left: 4px solid ${props => props.color};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
`;

const SummaryLabel = styled.div`
  font-size: 12px;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
`;

const SummaryValue = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #333;
  font-family: monospace;
`;

const SummarySubtext = styled.div`
  font-size: 12px;
  color: #999;
  margin-top: 5px;
`;

const ReportCard = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
`;

const ReportHeader = styled.div`
  padding: 30px;
  background: linear-gradient(135deg, #66bb6a 0%, #43a047 100%);
  color: white;
  text-align: center;
`;

const ReportTitle = styled.h2`
  font-size: 24px;
  margin-bottom: 8px;
`;

const ReportDate = styled.div`
  opacity: 0.9;
  font-size: 14px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableRow = styled.tr`
  &.hover:hover {
    background: #f5f5f5;
  }
`;

const TableCell = styled.td`
  padding: 12px 20px;
  border-bottom: 1px solid #eee;
`;

const SectionHeader = styled.tr`
  background: #f0f0f0;

  td {
    font-weight: 700;
    text-transform: uppercase;
    font-size: 12px;
    letter-spacing: 1px;
    color: #666;
    padding: 10px 20px;
  }
`;

const SubtotalRow = styled.tr<{ color: string }>`
  background: ${props => props.color};

  td {
    font-weight: 700;
    font-size: 14px;
    padding: 12px 20px;
  }
`;

const GrossProfitRow = styled.tr`
  background: rgba(25, 118, 210, 0.1);

  td {
    font-weight: 800;
    font-size: 15px;
    padding: 15px 20px;
    color: #1976d2;
  }
`;

const NetProfitRow = styled.tr<{ isProfit: boolean }>`
  background: ${props => (props.isProfit ? 'rgba(46,125,50,0.15)' : 'rgba(198,40,40,0.1)')};

  td {
    font-weight: 800;
    font-size: 16px;
    padding: 18px 20px;
    color: ${props => (props.isProfit ? '#2e7d32' : '#c62828')};
  }
`;

export default ProfitLoss;
